import { ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DeviceFormatterService,
  IDeviceContract,
  SnackbarData,
  SnackbarMessageType,
  SnackbarService,
} from '@nx-fe/services';
import { SspRestApiService } from 'sspapp/services/sspRestApiService';
import { XsI18nPipe } from '@nx-fe/pipes';
import { MemoryAndDateUtils } from 'sspapp/core/utils/memoryAndDateUtils';
import { MapStyle } from '@nx-fe/map';
import atlas, { Control, ControlPosition } from 'azure-maps-control';
import { SspDeviceAddressService } from 'sspapp/services/ssp-device-address.service';

@Component({
  selector: 'app-location',
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.scss', '../../../../../../../../assets/soti-tooltip.scss'],
})
export class LocationComponent {
  userMarkerIconUrl = 'assets/images/Map-Pointer-Grey.png';
  userMarker = new atlas.HtmlMarker();

  deviceMarker = new atlas.HtmlMarker();
  deviceMarkerIconUrl = 'assets/images/Map-Pointer-Red.png';
  deviceMarkerPopup = new atlas.Popup();

  currentZoom = 1;
  minZoom = 1;
  maxZoom = 22;

  mapInstance: atlas.Map;

  defaultOptions = {
    center: [0, 0],
    zoom: 1,
  };

  mapStyle: MapStyle = MapStyle.ROADMAP;

  device: IDeviceContract;
  deviceId: string;

  locateDeviceInProgress = false;
  lastKnownDeviceLocation: any;
  lastKnownDeviceLocationInfo: any;

  locateUserInProgress = false;
  currentUserLocation: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sspRestApiService: SspRestApiService,
    private deviceFormatterService: DeviceFormatterService,
    private xsI18nPipe: XsI18nPipe,
    private snackbarService: SnackbarService,
    private cdr: ChangeDetectorRef,
    private sspDeviceAddressService: SspDeviceAddressService
  ) {
    // Get device ID from route
    this.router.events.subscribe(_ => {
      this.deviceId = this.route.snapshot.parent.params.id;
    });

    this.sspRestApiService.getDeviceList().subscribe(devices => {
      const foundDevice = devices.filter(device => device.deviceId === this.deviceId);
      this.device = this.deviceFormatterService.contractToUI(foundDevice[0]);
      this.locateDevice();
    });
  }

  onMapReady(map: atlas.Map) {
    this.mapReady(map);
  }

  mapReady(map: atlas.Map) {
    this.mapInstance = map;
    this.mapInstance.resize();
    this.initializeControls();
    this.mapInstance.events.add('zoomend', event => {
      this.zoomChange(event);
    });
  }

  initializeControls() {
    const mapTypeButton: any = document.querySelector('ssp-map-view-type-button');
    const zoomButtons: any = document.querySelector('ssp-map-zoom-buttons');
    const locateDeviceButton: any = document.querySelector('ssp-map-locate-device-button');
    const locateUserButton: any = document.querySelector('ssp-map-locate-user-button');

    this.setControls([locateDeviceButton, locateUserButton, zoomButtons, mapTypeButton], ControlPosition.BottomRight);
  }

  setControls(buttons: any[], position: ControlPosition) {
    buttons.forEach(button => {
      const control: Control = {
        onAdd: (map: atlas.Map, options?: atlas.ControlOptions): HTMLElement => button,
        onRemove: (): void => {},
      };
      this.mapInstance.controls.add(control, {
        position: position,
      });
    });
  }

  toggleSatellite() {
    if (this.mapInstance.getStyle().style !== MapStyle.SATELLITE) {
      this.mapInstance.setStyle({ style: MapStyle.SATELLITE });
      this.mapStyle = MapStyle.SATELLITE;
    } else {
      this.mapInstance.setStyle({ style: MapStyle.ROADMAP });
      this.mapStyle = MapStyle.ROADMAP;
    }
  }

  zoomChange(event: atlas.MapEvent) {
    this.currentZoom = event.map.getCamera().zoom;
  }

  zoomOut(): void {
    if (this.currentZoom > this.minZoom) {
      this.currentZoom = this.mapInstance.getCamera().zoom - 1;
      this.mapInstance.setCamera({ zoom: this.currentZoom });
    }
  }

  zoomIn(): void {
    if (this.currentZoom < this.maxZoom) {
      this.currentZoom = this.mapInstance.getCamera().zoom + 1;
      this.mapInstance.setCamera({ zoom: this.currentZoom });
    }
  }

  zoomOnDeviceMarker() {
    this.currentZoom = 17;
    this.mapInstance.setCamera({
      center: [this.lastKnownDeviceLocation[0], this.lastKnownDeviceLocation[1]],
      zoom: this.currentZoom,
      type: 'ease',
    });
    setTimeout(() => {
      this.generateAndOpenPopup();
    }, 250);
  }

  zoomOnUserMarker() {
    this.currentZoom = 17;
    this.mapInstance.setCamera({
      center: [this.currentUserLocation[0], this.currentUserLocation[1]],
      zoom: this.currentZoom,
      type: 'ease',
    });
  }

  locateDevice() {
    this.locateDeviceInProgress = true;
    this.sspRestApiService.locateDevice(this.deviceId).subscribe(() => {
      setTimeout(() => {
        this.getLastDeviceLocation();
      }, 1000);
    });
  }

  getLastDeviceLocation() {
    this.sspRestApiService.getLastDeviceLocation(this.device.deviceId).subscribe(loc => {
      if (loc) {
        this.lastKnownDeviceLocation = [loc.longitude, loc.latitude];
        this.lastKnownDeviceLocationInfo = {
          timestamp: loc.collectionTimestamp,
          heading: loc.heading,
          speed: loc.speed,
        };

        this.setDeviceMarker();
        this.zoomOnDeviceMarker();
      } else {
        this.snackbarService.showSnackbar({
          type: SnackbarMessageType.failure,
          title:
            this.xsI18nPipe.transform('label_NotificationError') +
            ' ' +
            this.xsI18nPipe.transform('message_ActionErrorTitle', this.xsI18nPipe.transform('label_Locate')),
          message: this.xsI18nPipe.transform(
            'message_ActionGenericErrorText',
            this.xsI18nPipe.transform('label_Locate')
          ),
        } as SnackbarData);
      }
      this.locateDeviceInProgress = false;
    });
  }

  locateUser() {
    this.locateUserInProgress = true;
    navigator.geolocation.getCurrentPosition(position => {
      this.currentUserLocation = [position.coords.longitude, position.coords.latitude];
      this.setUserMarker();
      this.zoomOnUserMarker();
      this.locateUserInProgress = false;
    });
  }

  setDeviceMarker() {
    this.deviceMarker.setOptions({
      position: [this.lastKnownDeviceLocation[0], this.lastKnownDeviceLocation[1]],
      htmlContent: `<img src="${this.deviceMarkerIconUrl}" />`,
    });

    this.mapInstance.markers.add(this.deviceMarker);

    this.mapInstance.events.add('click', this.deviceMarker, this.zoomOnDeviceMarker.bind(this));
    this.mapInstance.events.add('mouseenter', this.deviceMarker, this.generateAndOpenPopup.bind(this));
    this.mapInstance.events.add('mouseleave', this.deviceMarker, this.closePopup.bind(this));
  }

  setUserMarker() {
    this.userMarker.setOptions({
      position: [this.currentUserLocation[0], this.currentUserLocation[1]],
      htmlContent: `<img src="${this.userMarkerIconUrl}" />`,
    });

    this.mapInstance.markers.add(this.userMarker);
    this.mapInstance.events.add('click', this.userMarker, this.zoomOnUserMarker.bind(this));
  }

  generateAndOpenPopup(): void {
    const lastLocatedTime =
      this.xsI18nPipe.transform('ssp_bing_map_Last_Located') +
      MemoryAndDateUtils.timeAgo(<any>new Date(this.lastKnownDeviceLocationInfo.timestamp), this.xsI18nPipe);

    this.sspDeviceAddressService
      .getAddress(this.lastKnownDeviceLocation[0], this.lastKnownDeviceLocation[1])
      .then(address => {
        this.deviceMarkerPopup.setOptions({
          position: this.lastKnownDeviceLocation,
          pixelOffset: [0, -25],
          closeButton: false,
          content: `
                <div class="info-popup">
                  <ul class="list-unstyled">
                      <li>
                          <h3>${this.device.deviceName}</h3>
                      </li>
                      <li>
                          <span>${address}</span>
                      </li>
                      <li>
                          <span>${lastLocatedTime}</span>
                      </li>
                  </ul>
                </div>
                `,
        });
        this.deviceMarkerPopup.open(this.mapInstance);
        this.cdr.detectChanges();
      });
  }

  closePopup() {
    this.deviceMarkerPopup.close();
  }
}
